// Import your station data
import { majorStations, minorStations } from "../data/stations.js";
import { postalCodes } from "../data/postalCodes.js";
import { regionPrefixes, regionMainStations } from "../data/regions.js";

export function getRegionDetails(regionName) {
  const regionData = regionMainStations[regionName];
  return regionData
}

export function postalCodeToRegion(postalCode) {
  const prefix = Number(postalCode.slice(0, 2))
  const regionName = regionPrefixes[prefix];
  const regionData = getRegionDetails(regionName)
  return regionData
}

export function getPostalCodeDetails(postalCode) {
  const codeNum = Number(postalCode);

  // 1. Exact hit
  let postalCodeData = postalCodes.find(
    code => Number(code.postalCode) === codeNum
  );
  if (postalCodeData) return postalCodeData;

  // 2. In range
  postalCodeData = postalCodes.find(
    code => codeNum >= Number(code.postalCodeMin) && codeNum <= Number(code.postalCodeMax)
  );
  if (postalCodeData) return postalCodeData;

  // 3. Closest number
  let closest = postalCodes.reduce((prev, curr) => {
    const prevDiff = Math.abs(codeNum - Number(prev.postalCode));
    const currDiff = Math.abs(codeNum - Number(curr.postalCode));
    return currDiff < prevDiff ? curr : prev;
  });

  return closest;
}
/**
 * Calculate distance between two points using Haversine formula
 * @param {number} lat1 - Latitude of first point
 * @param {number} lon1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lon2 - Longitude of second point
 * @returns {number} Distance in kilometers
 */
export function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Earth's radius in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Check if postal code is within a range
 * @param {string} postalCode - Postal code to check
 * @param {string} range - Range like "24100-24999"
 * @returns {boolean} True if postal code is within range
 */
function isPostalCodeInRange(postalCode, range) {
  if (!range || !postalCode) return false;

  const [start, end] = range.split("-");
  if (!start || !end) return false;

  const code = parseInt(postalCode);
  const startCode = parseInt(start);
  const endCode = parseInt(end);

  return code >= startCode && code <= endCode;
}

/**
 * Get station information by postal code
 * @param {string} postalCode - The postal code to search for
 * @param {Array} majorStations - Array of major bus stations
 * @param {Array} minorStations - Array of minor bus stations
 * @returns {Object|null} Station information with type, or null if not found
 */
export function getStationByPostalCode(postalCode) {
  if (!postalCode) return null;

  // First check for exact match in major stations
  for (const station of majorStations) {
    if (station.postalCode === postalCode) {
      return {
        type: "major",
        name: station.name,
        postalCode: station.postalCode,
        areaPostalCodeRange: station.areaPostalCodeRange || null,
        centerLat: station.lat || station.centerLat,
        centerLng: station.lng || station.centerLng,
        radiusKm: station.radiusKm || null,
        description: station.description || null,
        connections: station.connections || [],
        region: station.region || null,
      };
    }
  }

  // Then check for exact match in minor stations
  for (const station of minorStations) {
    if (station.postalCode === postalCode) {
      const regionalEstimate = getRegionalPostalCodeEstimateWithDetails(postalCode);

      return {
        type: "minor",
        name: station.name,
        postalCode: station.postalCode,
        closestSupportedStation: regionalEstimate.closestSupportedStation,
        areaPostalCodeRange: station.areaPostalCodeRange || null,
        centerLat: station.lat || station.centerLat,
        centerLng: station.lng || station.centerLng,
        radiusKm: station.radiusKm || null,
        description: station.description || null,
        connections: station.connections || [],
        region: station.region || null,
      };
    }
  }

  // Check if postal code falls within any major station's area range
  for (const station of majorStations) {
    if (
      station.areaPostalCodeRange &&
      isPostalCodeInRange(postalCode, station.areaPostalCodeRange)
    ) {
      return {
        type: "near major",
        name: `${station.name}`,
        postalCode: postalCode,
        closestSupportedStation: station.postalCode,
        areaPostalCodeRange: null,
        centerLat: station.lat || station.centerLat,
        centerLng: station.lng || station.centerLng,
        radiusKm: null,
        description: null,
        connections: [station.postalCode],
        region: station.region || null,
      };
    }
  }

  // If no exact match or range match, use regional estimate
  const regionalEstimate = getRegionalPostalCodeEstimateWithDetails(postalCode);
  if (regionalEstimate) {

    // If regional estimate found, return it
    return {
      type: "near region",
      name: null,
      postalCode: postalCode,
      closestSupportedStation: regionalEstimate.closestSupportedStation,
      areaPostalCodeRange: null,
      centerLat: regionalEstimate.centerLat,
      centerLng: regionalEstimate.centerLng,
      radiusKm: null,
      description: null,
      connections: regionalEstimate.connections,
      region: null,
    };
  }

  return null;
}

/**
 * Find nearest station to given coordinates
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {string|null} Postal code of nearest station
 */
function findNearestSupportedStation(lat, lng) {
  let nearestStation = null;
  let minDistance = Infinity;

  // Check major stations first
  for (const station of majorStations) {
    const stationLat = station.lat || station.centerLat;
    const stationLng = station.lng || station.centerLng;

    if (stationLat && stationLng) {
      const distance = calculateDistance(lat, lng, stationLat, stationLng);
      if (distance < minDistance) {
        minDistance = distance;
        nearestStation = station;
      }
    }
  }

  return nearestStation ? nearestStation.postalCode : null;
}

// Helper function to find region by postal code
export function getRegionalPostalCodeEstimateWithDetails(postalCode) {
  if (!postalCode) return null;


  // Find matching region
  const region = postalCodeToRegion(postalCode);
  const regionLat = region.lat ;
  const regionLng = region.lng ;
  const cityName = region.name || "";
  
  // Find nearest station to the regional coordinates
  const nearestStationPostalCode = findNearestSupportedStation(regionLat, regionLng);

  return {
    name: "estimate region",
    postalCode: postalCode,
    areaPostalCodeRange: null,
    centerLat: regionLat,
    centerLng: regionLng,
    description: "estimated to region",
    region: `close to ${cityName}`,
    connections: [nearestStationPostalCode],
    closestSupportedStation:  nearestStationPostalCode,
  };
}

